import { RenderEngine } from "./RenderEngine";
import { Box3, PositionalAudio } from "three";
import { Grouping, PbrMaterial } from "threngine";
import { CarModelCameraList, CarModelConfig, ModelLoadController, CarModelDrivingAnimation, CarModelDrivingCamera } from "./ModelLoad/ModelLoadController";
import { Controller3D } from "./3DController";
import { isMobile } from "react-device-detect";
import { ConfiguratorModel } from "./ConfiguratorModel";
import { EventEmitter } from "events";
import { AudioSource } from "./AudioController";
import { EventController } from "./Event/EventController";

export class DrivingAnimationController {
  private driving:boolean = false;
  private braking:boolean = false;
  private drivingConfig:CarModelDrivingAnimation;
  private userCamIndex:number;
  // private userClickTimeOut:any = -1;
  private audioSource:AudioSource;
  private environmentAudioMuteToken:any|null = null;
  private oldEnvironment:number = 0;
  private oldSkydomeGroup:string;
  private didChangeEnvironmentAndSkydome:boolean = false;

  public static ENABLE_CHANGE:string = "enableChange";
  public static event:EventEmitter = new EventEmitter();

  public get enabled():boolean { return this.driving && !this.braking; }
  public get animating():boolean { return this.driving; }

  public constructor(
    private engine:RenderEngine,
    private modelController:ConfiguratorModel,
    private controller3D:Controller3D,
    private eventController:EventController
  ) {
    ModelLoadController.event.addListener(ModelLoadController.STATUS_LOADED, (config:CarModelConfig) => {
      this.drivingConfig = config.driving;
    });

    // document.addEventListener('touchstart', this.onUserMouseDown);
    // document.addEventListener('mousedown', this.onUserMouseDown);

    // document.addEventListener('touchend', this.onUserMouseUp);
    // document.addEventListener('mouseup', this.onUserMouseUp);
  }

  public toggle():void {
    if (this.driving) {
      this.stop();
    } else {
      this.start();
    }
  }

  public async start():Promise<void> {
    const renderService = this.engine.engine.services.render;
    const materialService = this.engine.engine.services.material;
    const configurator = this.engine.configurator;
    const config = configurator.config;

    if (this.driving) return;

    // Set this before any async operation... 
    this.driving = true;
    this.braking = false;
    DrivingAnimationController.event.emit(DrivingAnimationController.ENABLE_CHANGE);

    // Close any doors or trunk lid before starting the ride :^)
    if (this.controller3D.carOpen) {
      await this.controller3D.playAnimation();
    }

    const existingWheel = this.drivingConfig.wheels
    .map(w => this.engine.scene.getObjectByName(w))
    .find(w => w!==undefined);

    if (!existingWheel) {
      throw new Error("No wheel for driving animation found!");
    }

    const wheelYPos = existingWheel.position.y;

    const box = new Box3().setFromObject(existingWheel);
    const radius = (box.max.y - box.min.y) * 0.5;

    const driverGrouping = config.groupings.find(g => g.name === "Mannequin Male");
    await configurator.setVisible(driverGrouping.id, true);

    // Workaround to hide christmas tree during driving.
    // THIS WILL BREAK IF THE GEOMETRY IS NOT BOUND TO AN ENVIRONMENT
    this.eventController.onEnvironmentChange("");

    if (!this.controller3D.arController.xrEngine.active) {
      this.didChangeEnvironmentAndSkydome = true;
      await this.changeEnvironmentAndSkydome();
    } else {
      this.didChangeEnvironmentAndSkydome = false;
    }

    const roadGrouping = config.groupings.find(g => g.name === "Animated Road");
    const roadMaterialID = config.default_state.grouping_material.find(
      g => g.id === roadGrouping.id).material_id;
    const roadMaterial = await (materialService as any)
      ._getMaterial(roadMaterialID, roadGrouping.ao_texture_id) as PbrMaterial;
    // Use local-space triplanar mapping.
    // This is important for WebXR where the scene can be rotated and translated arbitrarily in world-space.
    roadMaterial.triplanarMapping.useWorldSpace = false;
    roadMaterial.triplanarMapping.applyValues();

    await configurator.setVisible(roadGrouping.id, true);
    await this.fadeRoadInOut(roadGrouping, true);

    if (this.audioSource) {
      // TODO: properly remove audio source from the scene
      this.audioSource.reset();
    }
    this.audioSource = this.controller3D.audioController.instantiatePositionalAudioSource(`${HASH}/audio/driving.mp3`, 'INTERIOR');
    this.audioSource.volume = 0.35;
    this.audioSource.play(true);
    if (this.modelController.environmentAudioSource) {
      this.environmentAudioMuteToken = this.modelController.environmentAudioSource.acquireMuteToken();
    }

    let t = 0;
    let first = true;
    let velocity = 0;
    let uptimeOld = 0;

    const userCameras = this.drivingConfig.userCameras;
    if (userCameras && userCameras.length >= 1) {
      this.setCamera(userCameras[0], true);
      this.userCamIndex = 0;
    }

    const tMax = this.drivingConfig.accelerationTime;
    const velMax = this.drivingConfig.maxVelocity;
    const brakeDecay = this.drivingConfig.brakeDecay;

    const token = this.controller3D.animationController.acquireToken();

    const callback = async (uptime:number) => {
      /* When the user enters the driving animation during XR,
       * we do not change the environment and skydome, as we always use the same environment
       * with a transparent background in XR. But when we leave XR while the driving is still active,
       * we need to switch to the environment and skydome that driving normally uses.
       */
      if (!this.controller3D.arController.xrEngine.active && !this.didChangeEnvironmentAndSkydome) {
        this.didChangeEnvironmentAndSkydome = true;
        this.changeEnvironmentAndSkydome();
      }

      if (first) {
        uptimeOld = uptime;
        first = false;
      }

      const frametime = uptime - uptimeOld;
      const dT = 60 * frametime / 1000;
      uptimeOld = uptime;

      if (!this.braking) {
        if (t < tMax) {
          velocity = Math.pow(t / tMax, 2.0) * velMax;
        } else {
          velocity = velMax;
        }
      } else {
        velocity *= Math.pow(brakeDecay, dT);
        
        // Phase motor noises out while breaking...
        this.audioSource.volume = Math.max(0, this.audioSource.volume - 0.0025 * dT);

        // Detect when the car stops moving.
        if (velocity <= 0.0001) {
          renderService.onBeforeRender.unsubsribe(callback);
          this.controller3D.animationController.releaseToken(token);
          DrivingAnimationController.event.emit(DrivingAnimationController.ENABLE_CHANGE);
          await this.fadeRoadInOut(roadGrouping, false);
          await configurator.setVisible(roadGrouping.id, false);
          await configurator.setVisible(driverGrouping.id, false);

          if (this.didChangeEnvironmentAndSkydome) {
            this.restoreEnvironmentAndSkydome();
          }
          
          this.driving = false;
          this.braking = false;

          if (this.audioSource) {
            this.audioSource.pause();
          }
          if (this.modelController.environmentAudioSource && this.environmentAudioMuteToken) {
            this.modelController.environmentAudioSource.releaseMuteToken(this.environmentAudioMuteToken);
            this.environmentAudioMuteToken = null;
          }
        }
      }

      t += frametime;

      const animateWheel = (wheelName:string) => {
        const wheel = this.engine.scene.getObjectByName(wheelName);
        if (wheel) {
          const random = Math.sin(133766642069 * t * dT) * 0.002;
          wheel.rotation.x -= velocity / radius * dT;
          wheel.position.y = wheelYPos + random * Math.pow(velocity / velMax, 3.0);
        }
      }

      this.drivingConfig.wheels.forEach(animateWheel);

      roadMaterial.triplanarMapping.y.offset[1] -= velocity * dT;
      roadMaterial.triplanarMapping.applyValues();
      return Promise.resolve();
    };

    renderService.onBeforeRender.subscribe(callback);
  }

  public stop():void {
    this.braking = true;
    this.controller3D.setCamera(CarModelCameraList.Orbit);
  }

  public async rotateCamLeft():Promise<void> {
    const userCameras = this.drivingConfig.userCameras;
    if (!userCameras || !this.enabled)
      return;
    this.userCamIndex = (this.userCamIndex + userCameras.length - 1) % userCameras.length;
    await this.setCamera(userCameras[this.userCamIndex], false);
  }

  public async rotateCamRight():Promise<void> {
    const userCameras = this.drivingConfig.userCameras;
    if (!userCameras || !this.enabled)
      return;
    this.userCamIndex = (this.userCamIndex + 1) % userCameras.length;
    await this.setCamera(userCameras[this.userCamIndex], false);
  }

  private async fadeRoadInOut(grouping:Grouping, _in:boolean):Promise<void> {    
    const delay = (ms:number) => new Promise((resolve) => setTimeout(resolve, ms));

    if (grouping.object_names.length != 1) {
      throw "DrivingAnimation: Couldn't locate road mesh.";
    }
    
    const mesh = this.engine.scene.getObjectByName(grouping.object_names[0]);
    if (_in) {
      let x = 0;
      while (x <= 1.0) {
        mesh.scale.x = x;
        x += 0.05;
        await delay(16.66);
      }
    } else {
      let x = 1;
      while (x >= 0.0) {
        mesh.scale.x = x;
        x -= 0.05;
        await delay(16.66);
      }
    }
  }

  private async setCamera(camera:CarModelDrivingCamera, first:boolean = false):Promise<boolean> {
    const time = first ? this.drivingConfig.initialCameraTime : camera.time;
    const name = isMobile && camera.mobile ? camera.mobile : camera.desktop;

    return this.controller3D.setCameraDirect(name, time, camera.easing);
  }

  private async changeEnvironmentAndSkydome():Promise<void> {
    const environment = this.modelController.environmentGroup;
    
    const newEnvironment = environment.selections.find(s => s.name === "Evening Road");  
    if (!newEnvironment) {
      throw "Cannot find environment: 'Evening Road'";
    }
  
    this.oldEnvironment = environment.currentSelection;
    
    environment.changeSelection(newEnvironment.id);

    const configurator = this.engine.configurator;
    const config = configurator.config;
    const skydomeSet = config.visibility_sets.find(v => v.name === "Toogle Skydome");

    if (!skydomeSet) {
      throw "Cannot find visibility set: 'Toogle Skydome'";
    }
    
    this.oldSkydomeGroup = configurator.state.visibility_sets.find(v => v.id === skydomeSet.id).visibility_group_id;
    
    const newSkydomeGroup = config.visibility_groups.find(v => v.name === "Skydome Driving");
    if (!newSkydomeGroup) {
      throw "Cannot find visibility group: 'Skydome Driving'";
    }
  
    await configurator.setVisibilitySet(skydomeSet.id, newSkydomeGroup.id);
  }

  private async restoreEnvironmentAndSkydome():Promise<void> {
    const environment = this.modelController.environmentGroup;
    const configurator = this.engine.configurator;
    const config = configurator.config;

    // Reset the skydome and environment to the original state.
    const skydomeSet = config.visibility_sets.find(v => v.name === "Toogle Skydome");
    if (!skydomeSet) {
      throw "Cannot find visibility set: 'Toogle Skydome'";
    }
    await configurator.setVisibilitySet(skydomeSet.id, this.oldSkydomeGroup);
    this.modelController.changeSelection(
      environment.groupID,
      environment.selections[this.oldEnvironment].id);
  }

  // private onTimeOut = () => {
  //   if (!this.driving)
  //     return;

  //   // TODO: find a better way to determine the orbit camera... this is ugly hackery.
  //   const orbit = this.drivingConfig.userCameras.find(c => c.desktop.indexOf("Orbit") !== -1);
  //   this.controller3D.setCameraDirect(
  //     isMobile && orbit.mobile ? orbit.mobile : orbit.desktop, orbit.time, orbit.easing);
  //   this.userClickTimeOut = -1;
  //   this.userCamIndex = 0;
  // }

  // private onUserMouseDown = () => {
  //   if (this.userClickTimeOut != -1) {
  //     clearTimeout(this.userClickTimeOut);
  //   }
  //   this.userClickTimeOut = -1;
  // }

  // private onUserMouseUp = () => {
  //   this.userClickTimeOut = setTimeout(this.onTimeOut, 5000);
  // }
}